import { bind, React } from '../../../Imports';
import { ICriticalSpeedServiceInjectedProps, CriticalSpeedService } from '$State/CriticalSpeedFreezerService';
import { Alert, Card, Snackbar, TextField, ToggleButton, ToggleButtonGroup } from 'MaterialUIComponents';
import { faCircleCheck, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { VideoRetention } from './VideoRetention';
import { AutoScoreEvents } from './AutoScoreEvents';
import { Box } from '@mui/material';
import { SharedTabs, SharedTab } from '$Components/Shared/SharedTabs/SharedTabs';
import { isEncompass } from '$Utilities/integrationPlatform';
import { canAccessUserRolesPage, RolesEnum, VerifyRole } from '$Externals/VerifyRole';
import { StyledPlainSubmitButton } from '$Components/VideoEvents/VideoEventList/VideoEventListStyles';
import { ConfirmDialog } from '$Components/Shared/ConfirmDialog';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';

interface IEventConfigurationProps {
    isConfirmDialogOpen?: boolean;
    onDirtyStateChange?: (isDirty: boolean) => void;
    resetSignal?: number; //used to reset speed/retention if the user cancels the change
    isAutoScoreSplitEnabled?: boolean;
}

interface IEventConfigurationState {
    speed: number;
    originalSpeed: number;
    snackOpen: boolean;
    snackMessage: string;
    snackSuccess: boolean;
    saveEnabled: boolean;
    toggleValue: 'videoRetention' | 'criticalEvents' | 'autoScore';
    isDirty: boolean;
    previousTabValue: string;
    isConfirmDialogOpen: boolean;
}

const styles = require('./EventConfigurations.scss') as {
    card: string;
    inlineInput: string;
    criticalSpeedRow: string;
    criticalSpeedInputGroup: string;
    content: string;
    innerContent: string;
    toggleGroup: string;
    activeToggleButton: string;
    inactiveToggleButton: string;
    inactiveGreyToggleButton: string;
    toggleButton: string;
    saveButton: string;
    iconSuccess: string;
    iconError: string;
    heading: string;
    contentGeotab: string;
    flexContainer: string;
};

type IEventConfigurationListProps = IEventConfigurationProps & ICriticalSpeedServiceInjectedProps;

class _EventConfigurations extends React.Component<IEventConfigurationListProps> {
    state: IEventConfigurationState = {
        speed: 0,
        originalSpeed: 0,
        snackOpen: false,
        snackMessage: '',
        snackSuccess: false,
        saveEnabled: true,
        toggleValue: 'videoRetention',
        isDirty: false,
        previousTabValue: '',
        isConfirmDialogOpen: false,
    };

    async componentDidMount(): Promise<void> {
        if (isEncompass) {
            await this.getSpeed();
        }
    }

    componentDidUpdate(prevProps: IEventConfigurationListProps): void {
        // Check if resetSignal changed
        if (prevProps.resetSignal !== this.props.resetSignal && this.props.resetSignal !== undefined) {
            // Reset to original value
            this.resetChanges();
        }
    }

    async getSpeed(): Promise<void> {
        await this.props.CriticalSpeedSettings.getCriticalSpeedSettings();

        const { getCriticalSpeedSettingsResults } = this.props.CriticalSpeedSettings.getState();
        const speedData = getCriticalSpeedSettingsResults.data?.speedThreshold ? getCriticalSpeedSettingsResults.data.speedThreshold : 10;
        if (speedData !== this.state.speed) this.setState({ speed: speedData, originalSpeed: speedData });
    }

    updateSpeed(input: string) {
        input = input.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
        input = input.slice(0, 2);
        const speed = Number(input) || 0;

        const willBeDirty = speed !== this.state.originalSpeed;

        this.setState({ speed, isDirty: willBeDirty }, () => {
            // Notify parent if dirty state changed
            this.props.onDirtyStateChange?.(willBeDirty);
        });
    }

    saveSpeed() {
        if (!this.state.isDirty) {
            return;
        }
        this.setState({ saveEnabled: false });
        this.props.CriticalSpeedSettings.updateCriticalSpeedSettings(this.state.speed)
            .then(() => {
                this.setState(
                    {
                        snackOpen: true,
                        snackMessage: 'Settings saved successfully.',
                        snackSuccess: true,
                        saveEnabled: true,
                        isDirty: false,
                        originalSpeed: this.state.speed,
                    },
                    () => {
                        // Notify parent that we're no longer dirty
                        this.props.onDirtyStateChange?.(false);
                    },
                );
            })
            .catch(() => {
                this.setState({
                    snackOpen: true,
                    snackMessage: 'Settings unable to be saved.',
                    snackSuccess: false,
                    saveEnabled: true,
                });
            });
    }

    // Add this method to reset changes
    resetChanges() {
        this.setState(
            {
                speed: this.state.originalSpeed,
                isDirty: false,
            },
            () => {
                // Notify parent we're no longer dirty
                if (this.props.onDirtyStateChange) {
                    this.props.onDirtyStateChange(false);
                }
            },
        );
    }

    @bind
    handleToggleChange(e: React.MouseEvent<HTMLElement>, value: 'videoRetention' | 'criticalEvents' | 'autoScore'): void {
        if (this.state.toggleValue === value || value === null) return;

        if (this.state.isDirty) {
            this.setState(() => ({
                isConfirmDialogOpen: true,
                previousTabValue: value,
            }));
        } else {
            this.setState({ toggleValue: value });
        }
    }

    @bind
    handleSnackClose(): void {
        this.setState({ snackOpen: false });
    }

    @bind
    handleSpeedChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.updateSpeed(e.target.value);
    }

    @bind
    handleSaveClick(): void {
        this.saveSpeed();
    }

    @bind
    handleRetentionDirtyStateChange(isDirty: boolean): void {
        this.props.onDirtyStateChange?.(isDirty);
        this.setState({
            isDirty: isDirty,
        });
    }

    // Handle dialog confirmation (user wants to discard changes)
    @bind
    handleConfirmDiscard(): void {
        const { previousTabValue } = this.state;

        this.setState(() => ({
            isConfirmDialogOpen: false,
            toggleValue: previousTabValue || 'videoRetention',
            previousTabValue: '',
        }));

        this.resetChanges();
    }

    // Handle dialog cancelation (user wants to keep editing)
    @bind
    handleCancelNavigation(): void {
        this.setState({
            isConfirmDialogOpen: false,
        });
    }
    @bind
    handleTabChange(event: React.ChangeEvent<object>, newTabValue: 'videoRetention' | 'autoScore') {
        if (this.state.toggleValue === newTabValue || newTabValue === null) return;

        if (this.state.isDirty) {
            this.setState(() => ({
                isConfirmDialogOpen: true,
                previousTabValue: newTabValue,
            }));
        } else {
            this.setState({ toggleValue: newTabValue });
        }
    }

    renderAutoScoreSplitOn(children: JSX.Element) {
        return (
            this.props.isAutoScoreSplitEnabled && (
                <VerifyRole allowedRole={[RolesEnum.videoSuperAdmin, RolesEnum.videoAdmin]}>{children}</VerifyRole>
            )
        );
    }

    renderSnackbar() {
        const { snackOpen, snackSuccess, snackMessage } = this.state;
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '500px',
                    position: 'relative',
                }}
            >
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={snackOpen}
                    onClose={this.handleSnackClose}
                    autoHideDuration={4000}
                    sx={{ position: 'absolute', top: '4px !important' }}
                >
                    <Alert
                        onClose={this.handleSnackClose}
                        severity={snackSuccess ? 'success' : 'error'}
                        icon={
                            <FontAwesomeIcon
                                className={snackSuccess ? styles.iconSuccess : styles.iconError}
                                icon={snackSuccess ? faCircleCheck : faExclamationTriangle}
                            />
                        }
                        sx={
                            snackSuccess
                                ? { border: '1px solid #b8e7d9', backgroundColor: '#cceee4', color: '#00573e' }
                                : { border: '1px solid #fdc7c8', backgroundColor: '#fed6d9', color: '#811b1f' }
                        }
                    >
                        {snackMessage}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }

    renderUnsavedChangesConfirmDialog() {
        const { isConfirmDialogOpen } = this.state;
        /* used when there are unsaved changes on EventConfigurations and the user tries to navigate away */
        return (
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                title={isEncompass ? 'Are you sure?' : 'Unsaved Changes'}
                contentMessage={
                    'Are you sure you want to ' + (isEncompass ? 'exit' : 'leave') + ' without saving? All of your changes will be lost.'
                }
                cancelButton={isEncompass ? 'No, Go Back' : 'Cancel'}
                acceptButton={isEncompass ? 'Yes, Exit' : 'Leave'}
                handleModalClosed={() => this.handleCancelNavigation()}
                handleSubmit={() => this.handleConfirmDiscard()}
                isAlert={isEncompass}
                alertType={AlertTypes.Alert}
                showIcon={isEncompass}
                isButtonAlert={isEncompass}
                maxWidth={isEncompass ? '504px' : '400px'}
            />
        );
    }

    render(): JSX.Element {
        const { toggleValue, speed, saveEnabled, isDirty } = this.state;
        if (isEncompass) {
            return (
                <div>
                    <ToggleButtonGroup
                        className={`${styles.toggleGroup}`}
                        exclusive={true}
                        value={toggleValue}
                        onChange={this.handleToggleChange}
                    >
                        <ToggleButton
                            value="videoRetention"
                            title="Video Retention"
                            className={`${styles.toggleButton} ${
                                toggleValue == 'videoRetention'
                                    ? styles.activeToggleButton
                                    : this.props.isAutoScoreSplitEnabled
                                      ? styles.inactiveGreyToggleButton
                                      : styles.inactiveToggleButton
                            }`}
                        >
                            Video Retention
                        </ToggleButton>

                        <ToggleButton
                            value="criticalEvents"
                            title="Critical Events"
                            className={`${styles.toggleButton} ${
                                toggleValue == 'criticalEvents'
                                    ? styles.activeToggleButton
                                    : this.props.isAutoScoreSplitEnabled
                                      ? styles.inactiveGreyToggleButton
                                      : styles.inactiveToggleButton
                            }`}
                        >
                            Critical Events
                        </ToggleButton>
                        {this.renderAutoScoreSplitOn(
                            <ToggleButton
                                value="autoScore"
                                title="Auto-Score"
                                className={`${styles.toggleButton} ${
                                    toggleValue == 'autoScore'
                                        ? styles.activeToggleButton
                                        : this.props.isAutoScoreSplitEnabled
                                          ? styles.inactiveGreyToggleButton
                                          : styles.inactiveToggleButton
                                }`}
                            >
                                Auto-Score
                            </ToggleButton>,
                        )}
                    </ToggleButtonGroup>

                    {toggleValue === 'videoRetention' ? (
                        <Card className={styles.card} variant="outlined">
                            <div className={styles.content}>
                                <div className={styles.heading}>Video Retention</div>
                                <VerifyRole allowedRole={[RolesEnum.videoSuperAdmin]}>
                                    <VideoRetention
                                        resetRetentionData={this.props.resetSignal}
                                        onDirtyStateChange={this.handleRetentionDirtyStateChange}
                                    />
                                </VerifyRole>
                            </div>
                        </Card>
                    ) : toggleValue === 'autoScore' ? (
                        this.renderAutoScoreSplitOn(
                            <Card className={styles.card} variant="outlined">
                                <div className={styles.content}>
                                    <AutoScoreEvents />
                                </div>
                            </Card>,
                        )
                    ) : (
                        <>
                            <Card className={styles.card} variant="outlined">
                                <div className={styles.content}>
                                    <div className={styles.heading}>Critical Speeding</div>
                                    <div className={styles.criticalSpeedRow}>
                                        <span>Generate critical speeding event if the vehicle speed exceeds the posted speed limit sign by</span>
                                        <span className={styles.criticalSpeedInputGroup}>
                                            <TextField
                                                sx={{ width: '42px' }}
                                                inputProps={{
                                                    style: {
                                                        padding: '6px',
                                                        textAlign: 'center',
                                                    },
                                                }}
                                                variant="outlined"
                                                value={speed}
                                                onChange={this.handleSpeedChange}
                                            />
                                            mph or greater.
                                        </span>
                                    </div>
                                    <div>
                                        <StyledPlainSubmitButton disabled={!saveEnabled || !isDirty} onClick={this.handleSaveClick}>
                                            Save Changes
                                        </StyledPlainSubmitButton>
                                    </div>
                                </div>
                            </Card>
                            {this.renderSnackbar()}
                        </>
                    )}
                    {this.renderUnsavedChangesConfirmDialog()}
                </div>
            );
        } else if (canAccessUserRolesPage([RolesEnum.videoSuperAdmin])) {
            return this.props.isAutoScoreSplitEnabled ? (
                <>
                    <SharedTabs
                        value={toggleValue}
                        flexContainerClassName={styles.flexContainer}
                        onChange={this.handleTabChange}
                        removeBackgroundColor
                    >
                        <SharedTab label="Video Retention" value="videoRetention" removeBackgroundColor />

                        <SharedTab label="Auto-Score" value="autoScore" removeBackgroundColor />
                    </SharedTabs>
                    <div className={styles.contentGeotab} style={toggleValue === 'autoScore' ? { alignItems: 'unset' } : {}}>
                        {toggleValue === 'autoScore' ? (
                            <AutoScoreEvents />
                        ) : (
                            <>
                                <div className={styles.heading}>Video Retention</div>
                                <VideoRetention
                                    resetRetentionData={this.props.resetSignal}
                                    onDirtyStateChange={this.handleRetentionDirtyStateChange}
                                />
                            </>
                        )}
                    </div>
                    {this.renderUnsavedChangesConfirmDialog()}
                </>
            ) : (
                <div className={styles.contentGeotab}>
                    <div className={styles.heading}>Video Retention</div>
                    <VideoRetention resetRetentionData={this.props.resetSignal} onDirtyStateChange={this.handleRetentionDirtyStateChange} />
                </div>
            );
        } else {
            return <div />;
        }
    }
}

export const EventConfigurations = CriticalSpeedService.inject(_EventConfigurations);
